import React, { useState, useEffect } from 'react';
import StageImportForm from './StageImport/StageImportForm';
import StageImportTable from './StageImport/StageImportTable';
import EditStageModal from './StageImport/EditStageModal';
import { importStage, buildImportFormData, fetchStagesList, fetchRoadsList, deleteStage, deleteRoad, updateStage } from '../../services/api/stageImportApi';
import './StageImport.css';

const StageImport = () => {
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [result, setResult] = useState(null);
    const [items, setItems] = useState([]);
    const [roads, setRoads] = useState([]);
    const [editingItem, setEditingItem] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [stagesList, roadsList] = await Promise.all([
                fetchStagesList(),
                fetchRoadsList()
            ]);
            setItems(stagesList);
            setRoads(roadsList);
        } catch (error) {
            console.error('Failed to load data:', error);
        }
    };

    const handleSubmit = async (formData) => {
        setLoading(true);
        setMessage({ type: '', text: '' });
        setResult(null);

        try {
            const apiFormData = buildImportFormData(formData);
            const response = await importStage(apiFormData);

            if (response.success) {
                setMessage({
                    type: 'success',
                    text: 'Import completed successfully!'
                });

                if (response.data) {
                    setResult(response.data);
                }

                await loadData();
            } else {
                setMessage({
                    type: 'error',
                    text: 'Import failed. Please check the console for details.'
                });
            }
        } catch (error) {
            console.error('Import error:', error);
            setMessage({
                type: 'error',
                text: `Error: ${error.message}`
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id, type) => {
        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            let response;
            if (type === 'stage') {
                response = await deleteStage(id);
            } else {
                response = await deleteRoad(id);
            }

            if (response.success) {
                setMessage({
                    type: 'success',
                    text: `${type === 'stage' ? 'Stage' : 'Road'} deleted successfully!`
                });

                await loadData();
            } else {
                setMessage({
                    type: 'error',
                    text: 'Delete failed. Please check the console for details.'
                });
            }
        } catch (error) {
            console.error('Delete error:', error);
            setMessage({
                type: 'error',
                text: `Error: ${error.message}`
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (item) => {
        setEditingItem(item);
    };

    const handleUpdateSubmit = async (id, type, data) => {
        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const response = await updateStage(id, type, data);

            if (response.success) {
                setMessage({
                    type: 'success',
                    text: 'Update successful!'
                });
                await loadData();
            } else {
                setMessage({
                    type: 'error',
                    text: 'Update failed.'
                });
            }
        } catch (error) {
            setMessage({
                type: 'error',
                text: `Update error: ${error.message}`
            });
        } finally {
            setLoading(false);
            setEditingItem(null);
        }
    };


    return (
        <div className="stage-import-container">
            {/* Import Form Card */}
            <div className="card">
                <h2 className="card-title">Import Stage (GPX → points)</h2>

                <StageImportForm
                    onSubmit={handleSubmit}
                    loading={loading}
                    roads={roads}
                />

                {/* Status Messages */}
                {message.text && (
                    <div className={`msg ${message.type === 'success' ? 'ok' : 'err'}`}>
                        {message.text}
                    </div>
                )}

                {/* Result Display */}
                {result && (
                    <div className="result-section">
                        <h3>Result</h3>
                        <pre className="result-json">
                            {JSON.stringify(result, null, 2)}
                        </pre>
                    </div>
                )}
            </div>

            {/* Recent Items Table */}
            <div className="card">
                <div className="card-header">
                    <h2 className="card-title">Ostatnie pozycje (Stage &amp; Road)</h2>
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={loadData}
                        disabled={loading}
                    >
                        <span className="material-icons-outlined">refresh</span>
                        Refresh
                    </button>
                </div>

                <StageImportTable
                    items={items}
                    onDelete={handleDelete}
                    onEdit={handleEditClick}
                    loading={loading}
                />

                <div className="help-text">
                    „Kind" = <strong>OS</strong> (odcinek) lub <strong>LIAISON</strong> (dojazdówka).
                    Dojazdówki importujesz identycznie jak OS – wybierasz sekcję, wrzucasz GPX
                    (lub wybierasz z biblioteki Road). W API i w froncie są rozdzielone na sekcje:
                    Odcinki (czerwone) i Dojazdówki (czarne).
                </div>
            </div>

            {/* Edit Modal */}
            {editingItem && (
                <EditStageModal
                    item={editingItem}
                    onClose={() => setEditingItem(null)}
                    onSave={handleUpdateSubmit}
                />
            )}
        </div>
    );
};

export default StageImport;
